import { graphql, useStaticQuery } from "gatsby";
import React, { useMemo } from "react";

import Image from "../../components/image";
import DownloadIcon from "../../images/icons/fi-rr-download.inline.svg";
import { convertToSlug } from "../../utils/convertToSlug";
import SubNavMenu from "./subNavMenu";

const GetIcon = React.memo(({ name, colorMode, imgClass = "" }) => {
  const { iconCollection } = useStaticQuery(graphql`
    {
      iconCollection: allFile(
        filter: {
          name: {
            in: [
              "fi-rr-download.inline"
              "blog-dark.inline"
              "blog-light.inline"
              "facebook-dark.inline"
              "facebook-light.inline"
              "google-scholar-logo-light"
              "google-scholar-logo-dark"
              "instagram-dark.inline"
              "instagram-light.inline"
              "linkedin-dark.inline"
              "linkedin-light.inline"
              "tiktok-dark.inline"
              "tiktok-light.inline"
              "youtube-dark.inline"
              "youtube-light.inline"
            ]
          }
        }
      ) {
        nodes {
          name
          publicURL
        }
      }
    }
  `);

  const icons =
    useMemo(() => iconCollection?.nodes, [iconCollection?.nodes]) ?? [];

  const transformColormode = colorMode === "planet" ? "dark" : "light";

  const icon = /download/gi.test(name)
    ? icons.filter((icon) => icon.name.includes(name)).shift()
    : icons
        .filter((icon) => icon.name.includes(transformColormode))
        .filter((icon) => icon.name.includes(name))
        .shift();

  if (!icon) {
    return <></>;
  }

  // Memoize icon rendering to prevent unnecessary re-computations
  const iconElement = useMemo(() => {
    return (
      <Image
        key={name}
        imageAlt={""}
        image={""}
        sourceUrl={icon?.publicURL}
        imageClass={imgClass}
      />
    );
  }, [name, imgClass, icon?.publicURL]);

  return iconElement;
});

GetIcon.displayName = "GetIcon";

const SubLink = ({ item }) => {
  return (
    <div className={`flex ${item?.linkClass ?? ""} pt-1 mt-2`}>
      {item.img}
      <a
        id={convertToSlug(item?.title ?? "")}
        className="text-xs text-black-lighter planet:text-gray-generic my-auto hover:text-white"
        href={item.url ?? "#"}
        title={item?.title ?? item}
        target={item?.target ?? ""}
        rel={item?.rel ?? ""}
      >
        {item.title ?? item}
      </a>
    </div>
  );
};
const ConnectLinks = ({ site, planetDomain, colorMode, globalFileLinks }) => {
  const brochures = site?.siteMetadata?.brochures;
  const socialmedia = site?.siteMetadata?.social_media;

  const blogUri = [planetDomain, "/blog/" ?? "#"].join("");

  const connectData = {
    "Stay up to date": [
      {
        title: "Read our blog",
        img: (
          <GetIcon
            name={"blog"}
            colorMode={colorMode}
            imgClass={"h-4 pr-1 block"}
          />
        ),
        url: blogUri
      },
      {
        title: "View our publications on ",
        img: (
          <GetIcon
            name={"scholar"}
            colorMode={colorMode}
            imgClass={"w-24 pl-1 block"}
          />
        ),
        url: socialmedia?.google_scholar,
        target: "_gviblank",
        rel: "nofollow",
        linkClass: "flex-row-reverse justify-end items-start items-center"
      }
    ],
    "Follow us": [
      {
        title: "Instagram",
        img: (
          <GetIcon
            name={"instagram"}
            colorMode={colorMode}
            imgClass={"h-auto w-5 block"}
          />
        ),
        target: "_gviblank",
        url: socialmedia?.instagram,
        linkClass: "flex gap-2 items-center xxs:p-0 xxs:mt-0"
      },
      {
        title: "TikTok",
        img: (
          <GetIcon
            name={"tiktok"}
            colorMode={colorMode}
            imgClass={"h-auto w-5 block"}
          />
        ),
        target: "_gviblank",
        url: socialmedia?.tiktok,
        linkClass: "flex gap-2 items-center xxs:p-0 xxs:mt-0"
      },
      {
        title: "YouTube",
        img: (
          <GetIcon
            name={"youtube"}
            colorMode={colorMode}
            imgClass={"h-auto w-5 block"}
          />
        ),
        target: "_gviblank",
        url: socialmedia?.youtube,
        linkClass: "flex gap-2 items-center xxs:p-0 xxs:mt-0"
      },
      {
        title: "LinkedIn",
        img: (
          <GetIcon
            name={"linkedin"}
            colorMode={colorMode}
            imgClass={"h-auto w-5 block"}
          />
        ),
        target: "_gviblank",
        url: socialmedia?.linkedin,
        linkClass: "flex gap-2 items-center xxs:p-0 xxs:mt-0"
      },
      {
        title: "Facebook",
        img: (
          <GetIcon
            name={"facebook"}
            colorMode={colorMode}
            imgClass={"h-auto w-5 block"}
          />
        ),
        target: "_gviblank",
        url: socialmedia?.facebook,
        linkClass: "flex gap-2 items-center xxs:p-0 xxs:mt-0"
      }
    ],
    "Download links": [
      {
        title: "Download a brochure",
        img: (
          <>
            {/* <GetIcon
              name={"download"}
              colorMode={colorMode}
              imgClass={
                "w-6 mr-4 fill-yellow-standard planet:fill-white block text-white"
              }
            /> */}
            <DownloadIcon className="w-6 mr-4 fill-yellow-standard planet:fill-white" />
          </>
        ),
        target: "_gviblank",
        url: brochures?.brochure_dl
      }
    ]
  };
  return (
    <>
      <div className="connect-with-us w-full gvi-dark-gray-color border-b pb-3 border-border-light planet:border-gray-lighter">
        <span className="h5 uppercase active-link !px-0 pb-3">
          Connect with us
        </span>
      </div>
      <div className="w-full flex lg:flex-col">
        <div className="mt-8 w-4/12 lg:w-full">
          <p className="text-base font-semibold text-black-lighter planet:text-white py-1">
            Stay up to date
          </p>
          {connectData["Stay up to date"] &&
            connectData["Stay up to date"].map((dataItem, index) => (
              <SubLink key={"stay-up-to-date-" + index} item={dataItem} />
            ))}
        </div>

        <div className="mt-8 w-4/12 lg:w-full flex flex-col gap-2">
          <div
            className="text-base font-semibold text-black-lighter planet:text-white"
            dangerouslySetInnerHTML={{ __html: "Follow us" }}
          />

          {connectData["Follow us"] &&
            connectData["Follow us"].map((dataItem, index) => (
              <SubLink key={"follow-us-" + index} item={dataItem} />
            ))}
        </div>
        <SubNavMenu
          className="mt-8 w-4/12 lg:w-full"
          data={connectData["Download links"]}
        />
      </div>
    </>
  );
};

export default React.memo(ConnectLinks);
